import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Grid, Paper, Typography, useTheme } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import { useTranslation } from 'react-i18next';
import PageHeader from '../../components/PageHeader';
import { useOrganizationStore } from '../../stores/organization.store';
import ArticleDetailContent from '../../components/help/ArticleDetailContent';
import HelpPageHeaderActions from '../../components/help/HelpPageHeaderActions';
import KnowledgeBaseArticleCard from '../../components/help/KnowledgeBaseArticleCard';
import {
  getKnowledgeArticleById,
  getKnowledgeArticleChildren,
  getKnowledgeArticleParentId,
} from '../../constants/knowledgeBaseArticles';
import {
  localizeKnowledgeArticle,
  localizeKnowledgeArticles,
} from '../../helpers/knowledge-base-localization.helper';
import { useHelpTypography } from '../../hooks/useHelpTypography';
import { helpI18n } from '../../i18n/help';
import { getKnowledgeBaseAction } from '../../utils/knowledgeBaseActions';

function ArticleHubIntro({ content }: { content: string }) {
  const theme = useTheme();
  const { bodySx, mediumWeight } = useHelpTypography();
  const paragraphs = content
    .split(/\n\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  return (
    <Paper
      sx={{
        borderRadius: '16px',
        p: 3,
        mb: 2.5,
        boxShadow: theme.shadows[1],
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#F8FAFC',
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '12px',
            bgcolor: `${theme.palette.primary.main}14`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <MenuBookOutlinedIcon sx={{ fontSize: 24, color: 'primary.main' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
          {paragraphs.map((paragraph, index) => {
            const isLast = index === paragraphs.length - 1;
            const isCallToAction = paragraphs.length > 1 && isLast;

            return (
              <Typography
                key={index}
                color={isCallToAction ? 'text.secondary' : 'text.primary'}
                sx={{
                  ...bodySx,
                  fontSize: isCallToAction ? '0.875rem' : bodySx.fontSize,
                  fontWeight: isCallToAction ? mediumWeight : bodySx.fontWeight,
                  mt: index > 0 ? (isCallToAction ? 2 : 1.5) : 0,
                  pt: isCallToAction ? 2 : 0,
                  borderTop: isCallToAction ? `1px solid ${theme.palette.divider}` : 'none',
                }}
              >
                {paragraph}
              </Typography>
            );
          })}
        </Box>
      </Box>
    </Paper>
  );
}

const KnowledgeBaseArticle: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('translation', { i18n: helpI18n });
  const { articleId } = useParams<{ articleId: string }>();
  const { selectedOrganization } = useOrganizationStore();

  const rawArticle = articleId ? getKnowledgeArticleById(articleId) : undefined;
  const article =
    rawArticle &&
    selectedOrganization &&
    rawArticle.organizationTypes.includes(selectedOrganization.organization_type)
      ? localizeKnowledgeArticle(rawArticle)
      : undefined;

  const childArticles =
    rawArticle && selectedOrganization
      ? localizeKnowledgeArticles(
          getKnowledgeArticleChildren(rawArticle, selectedOrganization.organization_type)
        )
      : [];
  const parentArticleId = articleId ? getKnowledgeArticleParentId(articleId) : undefined;
  const articleAction = useMemo(
    () => (articleId ? getKnowledgeBaseAction(articleId) : undefined),
    [articleId]
  );

  if (!selectedOrganization) {
    return (
      <Box>
        <Typography color="text.secondary">{t('knowledgeBase.selectOrganizationArticle')}</Typography>
      </Box>
    );
  }

  if (!article) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/knowledge-base')}
          sx={{ mb: 2 }}
        >
          {t('knowledgeBase.backToKnowledgeBase')}
        </Button>
        <Paper sx={{ borderRadius: '16px', p: 4, textAlign: 'center', boxShadow: theme.shadows[2] }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {t('knowledgeBase.articleNotFound')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('knowledgeBase.articleNotFoundHint')}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={article.title}
        onBack={() => navigate(parentArticleId ? `/knowledge-base/${parentArticleId}` : '/knowledge-base')}
        backLabel={
          parentArticleId
            ? t('knowledgeBase.backToParent')
            : t('knowledgeBase.backToKnowledgeBase')
        }
        actions={<HelpPageHeaderActions organizationId={selectedOrganization.id} />}
      />

      {childArticles.length > 0 ? (
        <Box>
          {article.content ? <ArticleHubIntro content={article.content} /> : null}
          <Grid container spacing={2}>
            {childArticles.map((child) => (
              <Grid item xs={12} sm={6} md={4} key={child.id}>
                <KnowledgeBaseArticleCard
                  articleId={child.id}
                  title={child.title}
                  viewDetailsLabel={t('knowledgeBase.viewDetails')}
                  onClick={() => navigate(`/knowledge-base/${child.id}`)}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5 }}>
          <ArticleDetailContent content={article.content} />
          {articleAction ? (
            <Button
              variant="contained"
              size="large"
              endIcon={
                <ArrowForwardRoundedIcon
                  sx={{
                    transform: theme.direction === 'rtl' ? 'scaleX(-1)' : 'none',
                  }}
                />
              }
              onClick={() => {
                const state: {
                  showAddHint?: boolean;
                  showSectionHints?: boolean;
                  showRowActionsHint?: boolean;
                  showDriverViewHint?: boolean;
                  showDriverEditAssignHint?: boolean;
                  showQrHint?: boolean;
                  showDateFilterHint?: boolean;
                  showStatusHint?: boolean;
                  showTableHint?: boolean;
                } = {};
                if (articleAction.showAddHint) state.showAddHint = true;
                if (articleAction.showSectionHints) state.showSectionHints = true;
                if (articleAction.showRowActionsHint) state.showRowActionsHint = true;
                if (articleAction.showDriverViewHint) state.showDriverViewHint = true;
                if (articleAction.showDriverEditAssignHint) state.showDriverEditAssignHint = true;
                if (articleAction.showQrHint) state.showQrHint = true;
                if (articleAction.showDateFilterHint) state.showDateFilterHint = true;
                if (articleAction.showStatusHint) state.showStatusHint = true;
                if (articleAction.showTableHint) state.showTableHint = true;
                navigate(
                  articleAction.path,
                  Object.keys(state).length > 0 ? { state } : undefined,
                );
              }}
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                px: 3,
                py: 1.25,
                fontWeight: 700,
              }}
            >
              {t(articleAction.labelKey)}
            </Button>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default KnowledgeBaseArticle;
